import React, { useEffect, useRef } from 'react';
import { Container, Typography, Box, Grid, Paper } from '@mui/material';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Context
import { useI18n } from '../../context/I18nContext';

// Registrando os componentes do Chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const SectionContainer = styled.section`
  padding: 5rem 0;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: var(--border-radius);
  margin: 2rem 0;
`;

const ChartContainer = styled(Paper)`
  padding: 1.5rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  height: 400px;
  display: flex;
  flex-direction: column;
`;

const ChartTitle = styled(Typography)`
  text-align: center;
  margin-bottom: 1rem;
  color: var(--primary-color);
`;

const ChartSource = styled(Typography)`
  text-align: center;
  font-style: italic;
  margin-top: 1rem;
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const DataSection: React.FC = () => {
  const { t } = useI18n();
  
  // Dados para o gráfico de população
  const populationData = {
    labels: ['Ceilândia', 'Samambaia', 'Plano Piloto', 'Taguatinga', 'Planaltina', 'Águas Claras', 'Recanto das Emas', 'Gama', 'Guará', 'Santa Maria'],
    datasets: [{
      label: '2024',
      data: [432927, 267254, 225287, 214282, 202990, 195926, 145865, 142434, 134002, 128666],
      backgroundColor: 'rgba(0, 96, 100, 0.7)',
      borderColor: 'rgba(0, 96, 100, 1)',
      borderWidth: 1
    }]
  };
  
  // Dados para o gráfico de crescimento
  const growthData = {
    labels: ['Águas Claras', 'Jardim Botânico', 'Vicente Pires', 'Itapoã', 'Sobradinho II', 'SCIA/Estrutural', 'Varjão', 'Fercal', 'SIA', 'Riacho Fundo II'],
    datasets: [{
      label: 'Crescimento (%)',
      data: [42.3, 38.7, 35.2, 31.8, 28.5, 25.9, 23.4, 21.2, 19.8, 18.5],
      backgroundColor: 'rgba(255, 82, 82, 0.7)',
      borderColor: 'rgba(255, 82, 82, 1)',
      borderWidth: 1
    }]
  };
  
  // Opções comuns para os gráficos
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'População'
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
      },
    },
  };
  
  // Opções específicas para o gráfico de crescimento
  const growthChartOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        title: {
          display: true,
          text: 'Crescimento (%)'
        }
      }
    }
  };
  
  return (
    <SectionContainer id="data">
      <Container>
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Box textAlign="center" sx={{ mb: 5 }}>
            <Typography variant="h2" color="primary" sx={{ position: 'relative', display: 'inline-block', mb: 4 }}>
              {t('data_title')}
              <Box sx={{ position: 'absolute', bottom: -10, left: 0, width: '100%', height: 3, bgcolor: 'primary.main' }} />
            </Typography>
            
            <Typography variant="h6" align="center" sx={{ maxWidth: '800px', margin: '1.5rem auto 0', color: '#555' }}>
              {t('data_subtitle')}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 2, mx: -2 }}>
            <Box sx={{ width: { xs: '100%', md: '50%' }, px: 2 }}>
              <motion.div
                initial={{ x: -50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <ChartContainer elevation={3}>
                  <ChartTitle variant="h5">
                    {t('chart1_title')}
                  </ChartTitle>
                  <Box sx={{ flexGrow: 1 }}>
                    <Bar data={populationData} options={chartOptions} />
                  </Box>
                </ChartContainer>
              </motion.div>
            </Box>
            
            <Box sx={{ width: { xs: '100%', md: '50%' }, px: 2 }}>
              <motion.div
                initial={{ x: 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <ChartContainer elevation={3}>
                  <ChartTitle variant="h5">
                    {t('chart2_title')}
                  </ChartTitle>
                  <Box sx={{ flexGrow: 1 }}>
                    <Bar data={growthData} options={growthChartOptions} />
                  </Box>
                </ChartContainer>
              </motion.div>
            </Box>
          </Box>
          
          <ChartSource variant="body2" sx={{ mt: 2 }}>
            {t('chart_source')}
          </ChartSource>
        </motion.div>
      </Container>
    </SectionContainer>
  );
};

export default DataSection;
